import Button from "../../components/Button";
import { getSession } from "../../lib/api";
import PasskeyEnrollmentCard from "../../components/PasskeyEnrollmentCard";

export type DispatcherRoute =
  | { name: "home" }
  | { name: "new-job" }
  | { name: "drivers" }
  | { name: "jobs" }
  | { name: "job"; id: string };

/**
 * DispatchHome — Unit #19 canCreateJobs gate.
 *
 * The server enforces the canCreateJobs flag on POST /jobs (403 when
 * disabled). The UI mirrors that gate by disabling the NEW JOB button
 * with an explanatory hint. The server is still the source of truth
 * — the UI flag is read from the JWT at sign time and may go stale
 * after a mid-session admin flip; the next sign-in refreshes it.
 */
export default function DispatchHome({
  onNavigate,
  onToast,
}: {
  onNavigate: (route: DispatcherRoute) => void;
  /** SPEC-68 §4.15 — forwarded to the shared enrollment card. */
  onToast?: (msg: string, kind?: "success" | "error") => void;
}) {
  const session = getSession();
  const canCreate = session?.user.canCreateJobs !== false; // default true
  return (
    <main className="surface dispatch-home">
      <header className="surface-header">
        <h1>Eagle Dispatch</h1>
        <p className="sub">Dispatcher console</p>
      </header>

      {/* SPEC-68 §4.15 — the shared enrollment card, the dispatcher host.
          Self-gating (fetches its own status; renders nothing unless
          canEnroll && !hasPasskey) — deliberately NO local eligibility
          computation here, so the two role paths cannot drift. Enrollment is
          presence-gated for dispatchers too (§3.2): nothing renders until the
          dispatcher has clocked in on the dispatch terminal. */}
      <PasskeyEnrollmentCard onToast={onToast} />

      <div className="home-buttons">
        {canCreate ? (
          <Button
            className="home-button"
            onClick={() => onNavigate({ name: "new-job" })}
          >
            <span className="home-button-title">New Job</span>
            <span className="home-button-hint">Create &amp; assign a job</span>
          </Button>
        ) : (
          <Button
            className="home-button"
            variant="secondary"
            disabled
            title="Job creation is disabled for this dispatcher. Contact the office."
          >
            <span className="home-button-title">New Job</span>
            <span className="home-button-hint">
              Job creation disabled — contact office
            </span>
          </Button>
        )}
        <Button
          variant="secondary"
          className="home-button"
          onClick={() => onNavigate({ name: "drivers" })}
        >
          <span className="home-button-title">Available Drivers</span>
          <span className="home-button-hint">Who can take a job now</span>
        </Button>
        <Button
          variant="secondary"
          className="home-button"
          onClick={() => onNavigate({ name: "jobs" })}
        >
          <span className="home-button-title">Current Jobs</span>
          <span className="home-button-hint">Active &amp; finished</span>
        </Button>
      </div>
    </main>
  );
}
